import React, { useState } from "react";
import { Calendar, List, MapPin } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Calendar as CalendarComponent } from "../ui/calendar";

export const EventsSection = ({ events = [] }) => {
  const [viewMode, setViewMode] = useState("list");
  const [selectedDate, setSelectedDate] = useState(new Date());

  const getCategoryColor = (category) => {
    const colors = {
      Performance: "bg-purple-500",
      Academic: "bg-blue-500",
      Sports: "bg-green-500",
      Arts: "bg-pink-500",
      Holiday: "bg-orange-500",
      Social: "bg-yellow-500"
    };
    return colors[category] || "bg-gray-500";
  };

  return (
    <section className="py-16 bg-gradient-to-b from-yellow-400 to-orange-400">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <h2 className="text-3xl font-bold text-[#002147]">Upcoming Events</h2>
          <div className="flex gap-2">
            <Button
              variant={viewMode === "list" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("list")}
              className={viewMode === "list" ? "bg-[#002147]" : ""}
              data-testid="events-list-view"
            >
              <List size={16} className="mr-2" /> List
            </Button>
            <Button
              variant={viewMode === "calendar" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("calendar")}
              className={viewMode === "calendar" ? "bg-[#002147]" : ""}
              data-testid="events-calendar-view"
            >
              <Calendar size={16} className="mr-2" /> Calendar
            </Button>
          </div>
        </div>

        {viewMode === "calendar" ? (
          <div className="grid lg:grid-cols-3 gap-8">
            <Card className="lg:col-span-1">
              <CardContent className="p-4">
                <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  className="rounded-md border-0"
                />
              </CardContent>
            </Card>
            <div className="lg:col-span-2">
              <h3 className="font-semibold mb-4">Events on {selectedDate?.toLocaleDateString()}</h3>
              <div className="space-y-4">
                {events.filter(e => new Date(e.date).toDateString() === selectedDate?.toDateString()).map((event, index) => (
                  <Card key={event.id || index} className="card-hover">
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className={`w-2 h-16 rounded-full ${getCategoryColor(event.category)}`}></div>
                      <div className="flex-1">
                        <h4 className="font-semibold">{event.title}</h4>
                        <p className="text-sm text-gray-500">{event.time} • {event.location}</p>
                      </div>
                      <Badge variant="outline">{event.category}</Badge>
                    </CardContent>
                  </Card>
                ))}
                {events.filter(e => new Date(e.date).toDateString() === selectedDate?.toDateString()).length === 0 && (
                  <p className="text-gray-500 text-center py-8">No events on this date</p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {events.length > 0 ? (
              events.slice(0, 10).map((event, index) => (
                <Card key={event.id || index} className="card-hover flex overflow-hidden">
                  <div className={`w-2 ${getCategoryColor(event.category)}`}></div>
                  <div className="flex-1 p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <Badge variant="outline" className="mb-2">{event.category}</Badge>
                        <h3 className="font-semibold text-lg">{event.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                      </div>
                      <div className="text-right text-sm">
                        <div className="font-semibold text-[#002147]">
                          {new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </div>
                        <div className="text-gray-500">{event.time}</div>
                      </div>
                    </div>
                    <div className="flex items-center mt-3 text-sm text-gray-500">
                      <MapPin size={14} className="mr-1" /> {event.location}
                    </div>
                  </div>
                </Card>
              ))
            ) : (
              <div className="md:col-span-2 text-center py-12 bg-white/80 rounded-xl">
                <Calendar className="mx-auto mb-4 text-[#002147]" size={48} />
                <h3 className="text-xl font-semibold text-[#002147] mb-2">No Upcoming Events Yet</h3>
                <p className="text-gray-600">Check back soon for school events, activities, and important dates.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default EventsSection;
